<template>
  <div class="w-full md:w-8 lg:w-8">
    <div class="w-full mb-5">
      <h3 class="Label mb-4 text-left">What School/College are you currently studying?</h3>
      <div class="flex flex-wrap justify-content-start gap-3">
        <InputText v-model="college" placeholder="Enter school/college here" size="small" variant="filled" class="w-full" />
      </div>
    </div>

    <div class="w-full mb-5">
      <h3 class="Label mb-4 text-left">What Grade or Level are you studying?</h3>
      <div class="flex flex-wrap justify-content-start gap-3">
        <Select v-model="selectedGrade" :options="gradeOptions" :loading="isLoadingGrades" size="small"
          optionLabel="name" variant="filled" placeholder="Select grade or level" class="w-full md:w-56 text-left" />
      </div>
    </div>

    <div class="w-full mb-5">
      <h3 class="Label mb-4 text-left">What Subjects do you need help with?</h3>
      <div class="flex flex-wrap justify-content-start gap-3">
        <MultiSelect v-model="selectedSubjects" :options="availableSubjects" class="w-full md:w-56 text-left" show-clear 
          :disabled="!selectedGrade || availableSubjects.length === 0" variant="filled" size="small" optionLabel="name" 
          :maxSelectedLabels="3" selectedItemsLabel="{0} subjects selected"
          :placeholder="!selectedGrade ? 'Please select a grade first to see available subjects' : 'Select subjects'"
        />
      </div>
    </div>

    <div class="w-full mb-5">
      <h3 class="Label mb-4 text-left">Which exam medium do you need help with?</h3>
      <div class="flex flex-wrap justify-content-start gap-3">
        <Select v-model="selectedExamMedium" :options="availableMediums" size="small" optionLabel="name" variant="filled"
          :disabled="!selectedGrade || availableMediums.length === 0"
          :placeholder="!selectedGrade ? 'Please select a grade first to see available mediums' : 'Select exam medium'"
          class="w-full md:w-56 text-left" />
      </div>
    </div>

    <Button label="Next" class="orange-button mt-6" @click="nextStep" />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, defineEmits } from "vue";
import InputText from "primevue/inputtext";
import Select from "primevue/select";
import MultiSelect from "primevue/multiselect";
import Button from "primevue/button";
import { notifyWarn } from "@/utils/notification";
import { gradeOptions, isLoadingGrades, fetchGradeOptions } from "@/utils/options";

const emit = defineEmits(["study-level-selected"]);

const college = ref(""); 
const selectedGrade = ref(null);
const selectedSubjects = ref([]); 
const selectedExamMedium = ref(null);

// Fetch data when the component mounts
onMounted(() => {
  fetchGradeOptions();
});

// Compute the subjects available specifically for the selected grade
const availableSubjects = computed(() => {
  return selectedGrade.value && selectedGrade.value.subjects 
    ? selectedGrade.value.subjects 
    : [];
});

// Compute the mediums available specifically for the selected grade
const availableMediums = computed(() => {
  return selectedGrade.value && selectedGrade.value.mediums
    ? selectedGrade.value.mediums
    : [];
});

// Watch for changes in selectedGrade and reset dependent fields
watch(selectedGrade, () => {
  selectedSubjects.value = [];
  selectedExamMedium.value = null;
});

const nextStep = () => {
  let hasErrors = false;
  
  if(!college.value){
    notifyWarn("Please enter your school/college");
    hasErrors = true;
  }
  if(!selectedGrade.value){
    notifyWarn("Please select your grade or level");
    hasErrors = true;
  }
  if(selectedSubjects.value.length === 0){
    notifyWarn("Please select at least one subject");
    hasErrors = true;
  }
  if (!selectedExamMedium.value) {
    notifyWarn("Please select your exam medium");
    hasErrors = true;
  }
  
  if(hasErrors){
    return;
  }
  
  emit("study-level-selected", {
    college: college.value,
    selectedGrade: selectedGrade.value,
    selectedExamMedium: selectedExamMedium.value,
    selectedSubjects: [...selectedSubjects.value],
  });
};
</script>

<style scoped>
.Select-multiple .p-multiselect {
  min-width: 200px;
}
</style>